import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductResponse } from "../../../../types/product.type";
import { numberWithCommas } from "../../../../utils";
import StatusDot from "../../shared/StatusDot";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CataloguePagination from "../../shared/Pagination";
import PriceBookTypeChip from "../../PriceBook/TypeChip";
import {
  STRIPE_BACKGROUND,
  priceBookDetailsPath,
} from "../../shared/constants";
import PricebookItemPrice from "../PricebookItemPrice";
import { COMPACT_QUERY } from "../../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface PriceBookInfoProps {
  productDetails: ProductResponse;
}

const COLUMN_COUNT = 6;
const LIMIT = 10;

export default function PriceBookInfo({ productDetails }: PriceBookInfoProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);

  const priceBooks = useMemo(
    () => productDetails.pricebookItems ?? [],
    [productDetails],
  );

  const totalCount = priceBooks.length;

  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return priceBooks.slice(start, start + limit);
  }, [priceBooks, page, limit]);

  const handleViewPriceBook = (priceBookUuid: string) => () => {
    navigate(priceBookDetailsPath(priceBookUuid));
  };

  return (
    <Paper
      sx={{
        mt: 3,
        padding: "16px 24px",
        [compact]: { mt: 2, padding: "12px 16px" },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        gap={2}
        mb={2}
      >
        <Typography variant="body1" fontWeight={600}>
          รายการสมุดราคา
        </Typography>
        <Typography variant="caption" color="text.secondary">
          สินค้านี้อยู่ใน {numberWithCommas(totalCount)} สมุดราคา
        </Typography>
      </Stack>
      <TableContainer>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TableRow>
              <TableCell align="center">หมายเลข</TableCell>
              <TableCell width="30%">ชื่อสมุดราคา</TableCell>
              <TableCell align="center">ประเภท</TableCell>
              <TableCell align="center">ราคาขาย</TableCell>
              <TableCell align="center">สถานะ</TableCell>
              <TableCell align="center">การดำเนินการ</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((priceBook, index) => (
                <TableRow
                  key={priceBook.pricebook_code}
                  sx={{
                    bgcolor: index % 2 === 1 ? STRIPE_BACKGROUND : "#fff",
                    "& td, & th": { padding: "6px" },
                  }}
                >
                  <TableCell align="center">
                    {priceBook.pricebook_code}
                  </TableCell>
                  <TableCell>{priceBook.pricebook_name ?? "-"}</TableCell>

                  <TableCell align="center">
                    <PriceBookTypeChip
                      type={priceBook.pricebook_type}
                      fullWidth
                    />
                  </TableCell>

                  <TableCell align="center">
                    <PricebookItemPrice item={priceBook} alignment="center" />
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" justifyContent="center">
                      <StatusDot status={priceBook.status} />
                    </Stack>
                  </TableCell>

                  {/* A price row can outlive its pricebook master row: it keeps
                      the code but has no pricebook page to open. */}
                  <TableCell align="center">
                    {priceBook.pricebook_uuid && (
                      <Tooltip title="ดูสมุดราคา">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleViewPriceBook(
                            priceBook.pricebook_uuid,
                          )}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลสมุดราคา
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
